/*
 * @file: 曝光
 * @author: xuyong08
 */
import 'intersection-observer';
import {loggerClick} from '@utils/ubcLogger/ubcAddEventListener';
import {Options} from '@typing/ubc/ubcLog.interface';
import {send} from '@utils/ubcLogger/send';
import {deduplicationManager, generateExposureKey} from './deduplication';

/**
 * 发送曝光日志
 * @param element DOM节点
 */
export function sendExposureLog(element: HTMLElement) {
    const showInfo = element.dataset ? element.dataset.log : element.getAttribute('data-log');
    if (showInfo) {
        try {
            const baseShowInfo = JSON.parse(showInfo);
            const {value, ext, page, type = 'view'} = baseShowInfo;
            
            if (value) {
                // 生成唯一键
                const exposureKey = generateExposureKey({
                    type,
                    value,
                    customKey: ext?.customKey // 支持自定义键
                });
                
                // 检查是否已上报
                if (deduplicationManager.hasReported(exposureKey)) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.log(`🔄 埋点已上报，跳过: ${exposureKey}`);
                    }
                    return;
                }
                
                // 标记为已上报并发送埋点
                deduplicationManager.markAsReported(exposureKey);
                send({
                    page,
                    type,
                    value,
                    ext
                });
            }
        } catch (error) {
            console.error(error);
        }
    }
}

export default class Exposure {
    // eslint-disable-next-line no-use-before-define
    static instance: Exposure;

    private observer: IntersectionObserver | undefined;

    private readonly hasShowElementSet = new WeakSet();

    private mutationObserver: MutationObserver | undefined;

    constructor(options?: Options) {
        if (!Exposure.instance) {
            Exposure.instance = this;
        }
        this.init(options);
        this.collectElements();
    }

    removeObserve() {
        this.observer && this.observer.disconnect();
        this.mutationObserver && this.mutationObserver.disconnect();
    }

    getInstance() {
        return Exposure.instance;
    }

    add(el: HTMLElement) {
        // 检查元素是否已经被观察
        if (this.hasShowElementSet.has(el)) {
            return;
        }

        // 确保元素确实有 data-log 属性
        if (el.hasAttribute('data-log')) {
            this.observer && this.observer.observe(el);
        }
    }

    collectElements(container: HTMLElement = document.body) {
        const logElements = container.querySelectorAll('[data-log]');
        const observerElements = Array.from(logElements) as HTMLElement[];
        observerElements.forEach(ele => {
            this.add(ele);
        });
    }

    private init(options?: Options) {
        // 创建 IntersectionObserver 实例
        this.observer = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach(item => {
                    const target = item.target as HTMLElement;
                    if (
                        item.intersectionRatio > 0 &&
                        item.isIntersecting &&
                        !this.hasShowElementSet.has(target)
                    ) {
                        if (target?.dataset?.log) {
                            const logData = JSON.parse(target.dataset.log);
                            const {type, value} = logData;
                            // 不配置type时默认展现和点击日志都会有
                            // 配置展现时发送展现日志
                            if (value && (type === 'view' || !type)) {
                                sendExposureLog(target);
                            }
                            // 配置点击或者时长时绑定日志
                            if (value && (type === 'clk' || type === 'timing' || !type)) {
                                const loggerClickListener = loggerClick();
                                loggerClickListener.add(target);
                            }
                        }
                        this.hasShowElementSet.add(target);
                        // 取消元素监听
                        observer.unobserve(target);
                    }
                });
            },
            Object.assign(
                {
                    root: null,
                    threshold: 0.1
                },
                options
            )
        );

        // 创建 MutationObserver 实例 结合IntersectionObserver 监听新元素 兼具处理动态加载内容
        this.mutationObserver = new MutationObserver(mutationsList => {
            let hasNewElements = false;
            const addedElements: HTMLElement[] = [];

            mutationsList.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) {
                        const element = node as HTMLElement;
                        // 检查新添加的元素是否有 data-log 属性
                        if (element.hasAttribute('data-log')) {
                            hasNewElements = true;
                            addedElements.push(element);
                        }
                        // 检查新添加元素的子元素
                        const childElements = element.querySelectorAll('[data-log]');
                        if (childElements.length > 0) {
                            hasNewElements = true;
                            addedElements.push(...(Array.from(childElements) as HTMLElement[]));
                        }
                    }
                });
            });

            // 只有在确实有新元素时才处理
            if (hasNewElements) {
                addedElements.forEach(element => {
                    if (!this.hasShowElementSet.has(element)) {
                        this.observer?.observe(element);
                    }
                });
            }
        });

        // 配置 MutationObserver
        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-log'] // 只监听 data-log 属性的变化
        });
    }
}
